import { Box, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Loading, useGetList } from 'react-admin';
import type { Market, PageSchema } from '../dataProvider';
import { BLOCK_TYPE_LABEL } from './pageSchemaModel';
import {
  SCHEDULE_COLOR,
  SCHEDULE_LABEL,
  blockScheduleState,
  formatBlockWindow,
  hasWindow,
} from './blockSchedule';

/**
 * The market-wide schedule calendar (D-page-schema-editor-8d). A read-only campaign view:
 * EVERY scheduled (windowed) DRAFT block across the in-scope market's pages on one
 * date-ordered list, each with its page, its window and its derived state. The whole
 * season at a glance, not one page at a time. OPERATIONAL market-scoping applies (the
 * markets the viewer's scope permits, passed in); a uk editor sees uk schedules. It is
 * a light list off blockScheduleState, NOT a pixel scheduler. Server-side serve-time
 * filtering is the migration seam.
 */
export const MarketScheduleCalendar = ({
  markets,
  now,
}: {
  markets: readonly Market[];
  now: number;
}) => {
  const { data: pages = [], isLoading } = useGetList<PageSchema>(
    'pageSchemas',
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: 'pageId', order: 'ASC' },
      filter: { market: markets },
    },
  );

  if (isLoading) return <Loading />;

  const entries = pages
    .flatMap((page) =>
      page.blocks
        .map((block) => ({
          page,
          block,
          window: page.blockSchedules?.[block.id],
        }))
        .filter((e) => hasWindow(e.window)),
    )
    .sort((a, b) =>
      (a.window?.visibleFrom ?? '').localeCompare(b.window?.visibleFrom ?? ''),
    );

  return (
    <Card sx={{ mb: 2 }} data-testid="market-calendar">
      <CardContent>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
          Schedule calendar
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Every scheduled draft block across this market&apos;s pages. The apps
          serve each block time-filtered once its page is published.
        </Typography>
        {entries.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="market-calendar-empty"
          >
            No scheduled blocks in this market.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {entries.map(({ page, block, window }) => {
              const state = blockScheduleState(window, now);
              return (
                <Box
                  key={`${page.id}-${block.id}`}
                  data-testid={`calendar-entry-${page.id}-${block.id}`}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                  >
                    <Chip
                      size="small"
                      variant="outlined"
                      color={SCHEDULE_COLOR[state]}
                      label={SCHEDULE_LABEL[state]}
                    />
                    <Typography variant="body2">
                      {page.pageId} {String(page.market).toUpperCase()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {BLOCK_TYPE_LABEL[block.type]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatBlockWindow(window)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
